import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, MessageCircle, Menu as MenuIcon, X } from 'lucide-react';
import { pagePaths } from '../utils/sitePaths';

const Navigation = ({ currentPage, setCurrentPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeBtnRef = useRef(null);

  const openWhatsApp = useMemo(
    () => () => {
      const message =
        "Hi Ubuntu Enterprises! I need full-service event support in Pretoria/Gauteng. Please share availability and pricing.";
      const whatsappUrl = `https://wa.me/27663286231?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    },
    []
  );

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus close button for keyboard users.
    setTimeout(() => closeBtnRef.current?.focus?.(), 50);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isMenuOpen]);

  const navItems = [
    { id: 'home', label: '🏠 Home', page: 'home' },
    { id: 'menu', label: '🍽 Catering', page: 'menu' },
    { id: 'decor', label: '🎨 Decor', page: 'decor' },
    { id: 'spitbraai', label: '🔥 Spit Braai', page: 'spitbraai' },
    { id: 'teambuilding', label: '🤝 Team Building', page: 'teambuilding' },
    { id: 'sound', label: '🎧 Sound Hire', page: 'sound' },
    { id: 'venuesupport', label: '🏟 Venue Support', page: 'venuesupport' },
    { id: 'estimate', label: '💰 Estimate & Book', page: 'estimate' },
    { id: 'blog', label: '📝 Blog', page: 'blog' },
    { id: 'contact', label: '📞 Contact', page: 'contact' },
  ];

  const handleNavClick = (page, event) => {
    if (event) event.preventDefault();
    setCurrentPage(page);
    setIsMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex-shrink-0"
          >
            <a
              href={pagePaths.home}
              onClick={(e) => handleNavClick('home', e)}
              className="flex items-center gap-3 group"
              aria-label="UBUNTU EVENTS MANAGEMENT – Home"
            >
              <img
                src="/ubuntu-events-management-logo.png"
                alt="UBUNTU EVENTS MANAGEMENT logo"
                className="h-10 w-auto object-contain"
              />
              <span className="text-lg sm:text-xl font-playfair font-bold text-gold leading-tight group-hover:opacity-90">
                UBUNTU EVENTS MANAGEMENT
              </span>
            </a>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-3 flex items-baseline space-x-5">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={pagePaths[item.page]}
                  onClick={(e) => handleNavClick(item.page, e)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    currentPage === item.page
                      ? 'text-gold bg-gold/10'
                      : 'text-gray-700 hover:text-gold hover:bg-gold/5'
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>

          {/* Mobile quick actions + menu button */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => handleNavClick('estimate')}
              className={`inline-flex items-center justify-center w-10 h-10 rounded-full shadow-lg transition-colors ${
                currentPage === 'estimate'
                  ? 'bg-gold text-white'
                  : 'bg-gold/10 text-gold hover:bg-gold/20'
              }`}
              aria-label="Go to Estimate & Book"
              title="Estimate & Book"
            >
              <CalendarDays size={18} />
            </button>

            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="relative text-white hover:text-white focus:outline-none focus:text-white w-10 h-10 inline-flex items-center justify-center rounded-full bg-orange-500 hover:bg-orange-400 border border-orange-300 shadow-[0_0_0_3px_rgba(249,115,22,0.25)] transition-colors"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-nav-drawer"
              whileTap={{ scale: 0.92 }}
            >
              <motion.span
                className="absolute inset-0 rounded-full border border-gold/30"
                animate={
                  isMenuOpen
                    ? { scale: 1.08, opacity: 1 }
                    : { scale: [1, 1.08, 1], opacity: [0.5, 0.15, 0.5] }
                }
                transition={
                  isMenuOpen
                    ? { duration: 0.2 }
                    : { duration: 1.8, repeat: Infinity, ease: 'easeInOut' }
                }
              />
              <motion.span
                key={isMenuOpen ? 'close-icon' : 'menu-icon'}
                initial={{ opacity: 0, scale: 0.7, rotate: -20 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.7, rotate: 20 }}
                transition={{ duration: 0.2 }}
                className="relative z-10"
              >
                {isMenuOpen ? <X size={22} /> : <MenuIcon size={22} />}
              </motion.span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-50 md:hidden"
              onClick={() => setIsMenuOpen(false)}
              aria-hidden="true"
            />

            <motion.aside
              id="mobile-nav-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile navigation"
              initial={{ x: 26, y: -16, opacity: 0, scale: 0.96 }}
              animate={{ x: 0, y: 0, opacity: 1, scale: 1 }}
              exit={{ x: 26, y: -16, opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl z-50 md:hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <a
                  href={pagePaths.home}
                  onClick={(e) => handleNavClick('home', e)}
                  className="flex items-center gap-3 min-w-0 group"
                  aria-label="Home"
                >
                  <img
                    src="/ubuntu-events-management-logo.png"
                    alt="UBUNTU EVENTS MANAGEMENT logo"
                    className="h-9 w-auto object-contain"
                  />
                  <div className="min-w-0">
                    <div className="text-sm text-gray-500 font-semibold">Premium event services</div>
                    <div className="text-gold font-playfair font-bold text-sm truncate leading-tight group-hover:opacity-90">
                      UBUNTU EVENTS MANAGEMENT
                    </div>
                  </div>
                </a>

                <button
                  ref={closeBtnRef}
                  onClick={() => setIsMenuOpen(false)}
                  className="w-10 h-10 rounded-full hover:bg-gold/10 text-gray-700 inline-flex items-center justify-center transition-colors"
                  aria-label="Close menu"
                >
                  <X size={22} />
                </button>
              </div>

              <div className="p-4">
                <div className="bg-gradient-to-r from-gold/10 to-olive/10 border border-gold/20 rounded-2xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-gray-800 font-semibold">Quick Actions</div>
                      <div className="text-sm text-gray-600">Fast access from your phone</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        openWhatsApp();
                      }}
                      className="w-full btn-secondary text-sm py-3 flex items-center justify-center space-x-2"
                    >
                      <MessageCircle size={16} />
                      <span>WhatsApp</span>
                    </button>

                    <button
                      onClick={() => handleNavClick('estimate')}
                      className="w-full btn-primary text-sm py-3 flex items-center justify-center space-x-2"
                    >
                      <CalendarDays size={16} />
                      <span>Estimate & Book</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-2 pb-6">
                <div className="px-2 pb-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2">
                    Explore
                  </div>
                </div>

                <div className="space-y-1 px-1">
                  {navItems.map((item) => (
                    <a
                      key={item.id}
                      href={pagePaths[item.page]}
                      onClick={(e) => handleNavClick(item.page, e)}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-colors duration-200 flex items-center justify-between ${
                        currentPage === item.page
                          ? 'bg-gold/10 text-gold border border-gold/20'
                          : 'text-gray-800 hover:bg-gold/10'
                      }`}
                    >
                      <span className="font-medium">{item.label}</span>
                      <span className="text-gray-400">›</span>
                    </a>
                  ))}
                </div>

                <div className="mt-6 px-2">
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 border border-gray-200 transition-colors flex items-center justify-center"
                  >
                    Back to top
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
